import { prettyDateTime } from "@app/date";
import {
  calcMetrics,
  cancelDatabaseOpsPoll,
  computeCostId,
  fetchBackupRp,
  fetchBackupsByDatabaseId,
  fetchCostsByDatabases,
  fetchDatabase,
  fetchDatabaseImageById,
  fetchDatabaseImages,
  fetchDiskById,
  fetchEndpointsByServiceId,
  fetchRelease,
  fetchService,
  hasDeployDatabaseImage,
  pollDatabaseOperations,
  selectDatabaseById,
  selectDatabaseImageById,
  selectDiskById,
  selectEndpointsByServiceId,
  selectEnvironmentById,
  selectServiceById,
} from "@app/deploy";
import { CONTAINER_PROFILES } from "@app/deploy/container/utils";
import { findLoaderComposite } from "@app/loaders";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import {
  databaseActivityUrl,
  databaseBackupsUrl,
  databaseClusterUrl,
  databaseCredentialsUrl,
  databaseDetailUrl,
  databaseEndpointsUrl,
  databaseMetricsUrl,
  databaseRecoveryUrl,
  databaseScaleUrl,
  databaseSettingsUrl,
  environmentDatabasesUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import { setResourceStats } from "@app/search";
import type { DeployDatabase, DeployService } from "@app/types";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Outlet, useParams } from "react-router-dom";
import { usePoller } from "../hooks";
import {
  Banner,
  COST_ESTIMATE_TOOLTIP_TEXT,
  CRUMB_VARIANTS,
  DetailPageHeaderView,
  EnabledStatus,
  IconInfo,
  IconX,
  LoadingSpinner,
  PitrStatusPopover,
  ResourceDetailBar,
  ResourceDetailItem,
  ResourceDetailsTable,
  ScaleRecsView,
  type TabItem,
} from "../shared";
import { ActiveOperationNotice } from "../shared/active-operation-notice";
import { DatabaseEolNotice } from "../shared/db/database-eol-notice";
import { StatusText } from "../shared/status-text";
import { AppSidebarLayout } from "./app-sidebar-layout";

export function DatabaseHeader({
  database,
  databasePrimary,
  service,
  isLoading,
}: {
  database: DeployDatabase;
  databasePrimary: DeployDatabase;
  service: DeployService;
  isLoading: boolean;
}) {
  // Query additional data that subpages need
  useQuery(fetchEndpointsByServiceId({ id: database.serviceId }));
  useQuery(fetchBackupsByDatabaseId({ id: database.id }));
  useQuery(fetchRelease({ id: service.currentReleaseId }));
  useQuery(fetchBackupRp({ envId: database.environmentId }));

  const metrics = calcMetrics([service]);
  useQuery(fetchDiskById({ id: database.diskId }));
  const disk = useSelector((s) => selectDiskById(s, { id: database.diskId }));
  useQuery(fetchDatabaseImages());
  const image = useSelector((s) =>
    selectDatabaseImageById(s, { id: database.databaseImageId }),
  );
  const pitrStatusLabel =
    hasDeployDatabaseImage(image) && !image.pitrSupported
      ? database.type === "postgresql"
        ? "Version unsupported"
        : "Database unsupported"
      : "Disabled";

  const orgId = useSelector(selectOrganizationSelectedId);
  useQuery(fetchCostsByDatabases({ orgId }));
  const { isLoading: isCostLoading } = useLoader(fetchCostsByDatabases);
  const cost = useSelector((s) =>
    schema.costs.selectById(s, { id: computeCostId("Database", database.id) }),
  );

  return (
    <div className="flex flex-col gap-4">
      {!!database.deletedAt && (
        <Banner className="mt-2 mb-4" variant="warning">
          This database was deprovisioned on{" "}
          {prettyDateTime(database.deletedAt)}.
        </Banner>
      )}
      <ResourceDetailBar
        isLoading={isLoading}
        icon={
          database.type === "" ? (
            <LoadingSpinner />
          ) : (
            <img
              src={`/database-types/logo-${database.type}.png`}
              className="w-[32px] h-[32px]"
              aria-label={`${database.type} Database`}
            />
          )
        }
        name={database.handle}
        id={database.id}
        createdAt={database.createdAt}
        cost={isCostLoading || cost.id === "" ? null : cost.estCost}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      {!database.deletedAt && (
        <ResourceDetailsTable columns={2}>
          <ResourceDetailItem title="Database Type" isLoading={image.id === ""}>
            {image.description}
          </ResourceDetailItem>
          <ResourceDetailItem title="Profile" isLoading={service.id === ""}>
            {CONTAINER_PROFILES[service.instanceClass]?.name}
          </ResourceDetailItem>
          <ResourceDetailItem title="Disk Size" isLoading={disk.id === ""}>
            {`${disk.size} GB`}
          </ResourceDetailItem>
          <ResourceDetailItem
            title="Memory Limit"
            isLoading={service.id === ""}
          >
            {`${metrics.totalMemoryLimit / 1024} GB`}
          </ResourceDetailItem>
          <ResourceDetailItem title="Disk Type" isLoading={disk.id === ""}>
            {disk.ebsVolumeType}
          </ResourceDetailItem>
          <ResourceDetailItem title="CPU Share" isLoading={service.id === ""}>
            {metrics.totalCPU}
          </ResourceDetailItem>
          <ResourceDetailItem title="Disk IOPS" isLoading={disk.id === ""}>
            {disk.provisionedIops}
          </ResourceDetailItem>
          <ResourceDetailItem title="Point-in-Time Recovery">
            {database.id === "" ? (
              <LoadingSpinner />
            ) : database.enablePitr ? (
              <EnabledStatus enabled />
            ) : (
              <div className="flex items-center gap-1">
                <StatusText
                  className={
                    hasDeployDatabaseImage(image) && !image.pitrSupported
                      ? "text-gray-500"
                      : "text-red"
                  }
                  icon={<IconX variant="sm" color="currentColor" />}
                >
                  {pitrStatusLabel}
                </StatusText>
                <PitrStatusPopover
                  database={database}
                  image={image}
                  trigger={
                    <IconInfo
                      className="inline-block opacity-50 hover:opacity-100"
                      variant="sm"
                    />
                  }
                />
              </div>
            )}
          </ResourceDetailItem>
          <ResourceDetailItem
            title="Disk Encryption"
            isLoading={disk.id === ""}
          >
            {`AES-${disk.keyBytes * 8}`}
          </ResourceDetailItem>
          <ResourceDetailItem
            title="Container Efficiency"
            isLoading={service.id === ""}
          >
            <ScaleRecsView service={service} />
          </ResourceDetailItem>
          {databasePrimary.id && (
            <ResourceDetailItem title="Replicates">
              <Link to={databaseDetailUrl(databasePrimary.id)} className="flex">
                {databasePrimary.handle}
              </Link>
            </ResourceDetailItem>
          )}
        </ResourceDetailsTable>
      )}
    </div>
  );
}

const DatabaseOperationNotice = ({ id }: { id: string }) => {
  const poller = useMemo(() => pollDatabaseOperations({ id }), [id]);
  const cancel = useMemo(() => cancelDatabaseOpsPoll(), []);
  usePoller({
    action: poller,
    cancel,
  });

  return <ActiveOperationNotice resourceId={id} resourceType="database" />;
};

function DatabasePageHeader() {
  const { id = "" } = useParams();
  const dispatch = useDispatch();

  const database = useSelector((s) => selectDatabaseById(s, { id }));
  const databasePrimary = useSelector((s) =>
    selectDatabaseById(s, { id: database.initializeFrom }),
  );
  const service = useSelector((s) =>
    selectServiceById(s, { id: database.serviceId }),
  );
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: database.environmentId }),
  );

  const loaderDb = useQuery(
    fetchDatabase({ id, withDeleted: true, waitForBootup: true }),
  );
  const loaderService = useQuery(fetchService({ id: database.serviceId }));
  const loaderEndpoints = useQuery(
    fetchEndpointsByServiceId({ id: database.serviceId }),
  );
  const loader = findLoaderComposite([
    loaderDb,
    loaderService,
    loaderEndpoints,
  ]);
  const dbEndpoints = useSelector((s) =>
    selectEndpointsByServiceId(s, { serviceId: database.serviceId }),
  );
  useQuery(fetchDatabaseImageById({ id: database.databaseImageId }));
  const databaseImage = useSelector((s) =>
    selectDatabaseImageById(s, { id: database.databaseImageId }),
  );

  useEffect(() => {
    dispatch(
      setResourceStats({
        id,
        type: "database",
        deletedAt: database?.deletedAt,
      }),
    );
  }, [database?.deletedAt]);

  const crumbs = [];
  if (environment.id) {
    crumbs.push({
      name: environment.handle,
      to: environmentDatabasesUrl(environment.id),
      label: "Environment",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    });
  } else {
    crumbs.push({
      name: "Unknown Environment",
      to: "#",
      label: "Environment",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    });
  }

  const tabs: TabItem[] = [];

  if (!database.deletedAt) {
    tabs.push(
      { name: "Metrics", href: databaseMetricsUrl(id) },
      { name: "Scale", href: databaseScaleUrl(id) },
      {
        name: "Endpoints",
        href: databaseEndpointsUrl(id),
        count: loader.isInitialLoading ? null : dbEndpoints.length,
      },
    );
  }

  tabs.push(
    { name: "Activity", href: databaseActivityUrl(id) },
    {
      name: "Backups",
      href: databaseBackupsUrl(id),
      enabled: database.enableBackups,
    },
  );

  if (!database.deletedAt) {
    tabs.push(
      { name: "Cluster", href: databaseClusterUrl(id) },
      { name: "Connection URLs", href: databaseCredentialsUrl(id) },
    );

    // Show recovery tab if the organization has PITR enabled on the DB
    if (databaseImage.pitrSupported && database.enablePitr) {
      tabs.push({ name: "Recovery", href: databaseRecoveryUrl(id) });
    }

    tabs.push({ name: "Settings", href: databaseSettingsUrl(id) });
  }

  return (
    <>
      {!database.deletedAt && (
        <>
          <DatabaseOperationNotice id={id} />
          <DatabaseEolNotice database={database} />
          {database.pitrStatus === "pending_restart" && (
            <Banner variant="warning" showIcon={true}>
              This database needs to be restarted to support Point-in-time
              recovery. Once restarted, PITR will be automatically enabled.
            </Banner>
          )}
          {/* // Currently disabled because of limited deployment and can be a bit forceful on the ui
          database.pitrStatus === "restricted" && (
            <Banner variant="warning" showIcon={true}>
              Point-in-time recovery (PITR) is unsupported for this database due
              to its extremely high write volume.
            </Banner>
          )*/}
        </>
      )}
      <DetailPageHeaderView
        {...loader}
        breadcrumbs={crumbs}
        titleLabel="Database"
        title={database ? database.handle : "Loading..."}
        detailsBox={
          <DatabaseHeader
            database={database}
            databasePrimary={databasePrimary}
            service={service}
            isLoading={loader.isLoading}
          />
        }
        tabs={tabs}
        lastBreadcrumbTo={databaseDetailUrl(database.id)}
      />
    </>
  );
}

export const DatabaseDetailLayout = () => {
  return (
    <AppSidebarLayout
      header={<DatabasePageHeader />}
      mainWrapperDivClassName="overflow-x-hidden"
    >
      <Outlet />
    </AppSidebarLayout>
  );
};
